import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";
import SoftSelect from "components/SoftSelect";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import { API_URL } from "config";
import FlagLoader from "layouts/flagloader";
import { useAuth } from "../../../AuthContext";

const parseStoredPermissions = () => {
  try {
    const stored = localStorage.getItem("permissions");
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const EditSubBranch = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();

  const [form, setForm] = useState({
    name: "",
    address: "",
    status: true,
    branch_id: "",
  });
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    color: "success",
  });

  const grantedPermissions = useMemo(
    () => Array.from(new Set([...permissions, ...userPermission, ...parseStoredPermissions()])),
    [permissions, userPermission]
  );

  const hasSubBranchPermission = useCallback(
    (...requiredPermissions) => {
      if (grantedPermissions.includes("access.system.full_access")) return true;
      return requiredPermissions
        .flat()
        .filter(Boolean)
        .some((permission) => grantedPermissions.includes(permission));
    },
    [grantedPermissions]
  );

  const canViewPage = hasSubBranchPermission("sub_branch.pages.edit.view");
  const canSubmit =
    hasSubBranchPermission("sub_branch.actions.update") &&
    hasSubBranchPermission("sub_branch.actions.submit_update");
  const canViewField = useCallback(
    (field) => hasSubBranchPermission(`sub_branch.fields.${field}.view`),
    [hasSubBranchPermission]
  );

  useEffect(() => {
    if (!isAuthReady) return;

    if (!canViewPage) {
      setPageLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const token = localStorage.getItem("authToken");

        const res = await fetch(`${API_URL}/sub-branches/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Error loading data");

        if (canViewField("branch")) {
          const branchRes = await fetch(`${API_URL}/sub-branches/branch-options`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const branchData = await branchRes.json();
          if (!branchRes.ok) throw new Error(branchData.message || "Error loading branches");
          setBranches(branchData.data || branchData);
        }

        setForm({
          name: data.data.name || "",
          address: data.data.address || "",
          status: data.data.status == 1,
          branch_id: data.data.branch_id ? Number(data.data.branch_id) : "",
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.message || "Error loading data",
          color: "error",
        });
      } finally {
        setPageLoading(false);
      }
    };

    fetchData();
  }, [canViewField, canViewPage, id, isAuthReady]);

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!canSubmit) {
      setSnackbar({
        open: true,
        message: "You do not have permission to update sub branches.",
        color: "error",
      });
      return;
    }

    if (!form.name || !form.branch_id) {
      setSnackbar({
        open: true,
        message: "Name & Branch required",
        color: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("authToken");

      const res = await fetch(`${API_URL}/sub-branches/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: form.name,
          address: form.address,
          branch_id: form.branch_id,
          status: form.status ? 1 : 0,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Update failed");

      setSnackbar({
        open: true,
        message: "Sub Branch updated successfully",
        color: "success",
      });

      setTimeout(() => navigate("/sub-branches"), 1200);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Update failed",
        color: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthReady || pageLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="60vh">
          <FlagLoader />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Edit Sub Branch
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to edit sub branches.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  const branchOptions = branches.map((branch) => ({
    value: Number(branch.id),
    label: branch.name,
  }));
  const selectedBranch = branchOptions.find((option) => option.value === Number(form.branch_id)) || null;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Sub Branch"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar({ ...snackbar, open: false })}
      />

      <SoftBox p={3}>
        <SoftTypography variant="h5" mb={2}>
          Edit Sub Branch
        </SoftTypography>

        <form onSubmit={handleUpdate}>
          {canViewField("branch") && (
            <SoftBox mb={2}>
              <SoftSelect
                size="small"
                placeholder="Select Branch"
                options={branchOptions}
                value={selectedBranch}
                onChange={(option) =>
                  setForm((prev) => ({
                    ...prev,
                    branch_id: option ? option.value : "",
                  }))
                }
              />
            </SoftBox>
          )}

          {canViewField("name") && (
            <SoftBox mb={2}>
              <SoftInput
                placeholder="Sub Branch Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </SoftBox>
          )}

          {canViewField("address") && (
            <SoftBox mb={2}>
              <SoftInput
                placeholder="Address"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </SoftBox>
          )}

          {canViewField("status") && (
            <SoftBox display="flex" alignItems="center" mb={2}>
              <Switch checked={form.status} onChange={() => setForm({ ...form, status: !form.status })} />
              <SoftTypography ml={1}>{form.status ? "Active" : "Inactive"}</SoftTypography>
            </SoftBox>
          )}

          {canSubmit && (
            <SoftButton
              type="submit"
              color="info"
              disabled={loading}
              endIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {loading ? "Processing..." : "Update Sub Branch"}
            </SoftButton>
          )}
        </form>
      </SoftBox>
    </DashboardLayout>
  );
};

export default EditSubBranch;
